<template>
  <el-dialog v-model="visible" :title="props.editData ? 'LC 수정' : '신규 LC'" width="800px" destroy-on-close>
    <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="LC번호" prop="lc_number">
            <el-input v-model="form.lc_number" placeholder="예: LC-2024-001" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="유형" prop="lc_type">
            <el-select v-model="form.lc_type" style="width: 100%">
              <el-option label="At Sight (일람불)" value="AT_SIGHT" />
              <el-option label="Usance (기한부)" value="USANCE" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="법인" prop="company">
            <el-select v-model="form.company" filterable clearable placeholder="법인 선택" style="width: 100%"
              @change="onCompanySelected">
              <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="메인오더 (선택)" prop="main_request">
            <el-select v-model="form.main_request" placeholder="메인오더 선택" filterable style="width: 100%">
              <el-option
                v-for="mr in mainRequests"
                :key="mr.id"
                :label="`${mr.request_number} - ${mr.brand_name}`"
                :value="mr.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item label="개설 한도계좌 (차입금, 선택)" prop="facility_account">
            <el-select v-model="form.facility_account" placeholder="차입금 계좌 선택" filterable clearable
              style="width: 100%" @change="onFacilitySelected">
              <el-option
                v-for="acc in filteredFacilityAccounts"
                :key="acc.id"
                :label="facilityLabel(acc)"
                :value="acc.id"
              />
            </el-select>
            <!-- 잔여한도 미리보기 (2026-07-09) — 선택한 한도계좌의 설정/사용/잔여 + 이 LC 반영 후 잔여 -->
            <div v-if="facilityPreview" class="facility-preview">
              설정한도 {{ fmtLimit(facilityPreview.limit) }} · 사용한도 {{ fmtLimit(facilityPreview.used) }} ·
              잔여한도 <b>{{ fmtLimit(facilityPreview.available) }}</b> {{ facilityPreview.currency }}
              <template v-if="facilityPreview.alreadyCounted">
                — 이 LC는 이미 사용한도에 반영되어 있습니다
              </template>
              <template v-else>
                → 이 LC({{ fmtLimit(form.amount) }}) 개설 반영 후 잔여
                <b :class="{ 'fp-over': facilityPreview.over }">{{ fmtLimit(facilityPreview.after) }}</b>
                <span v-if="facilityPreview.over" class="fp-over"> · 한도 초과</span>
              </template>
              <span v-if="facilityPreview.currencyMismatch" class="fp-over">
                · 통화 불일치 (LC {{ form.currency }} ≠ 계좌 {{ facilityPreview.currency }})</span>
            </div>
            <div v-else-if="form.facility_account === null || form.facility_account === ''" class="facility-preview fp-hint">
              한도계좌를 선택하면 개설 시 잔여한도에 자동 반영됩니다 — 미선택 시 한도 관리에서 빠집니다.
            </div>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="통화" prop="currency">
            <el-select v-model="form.currency" style="width: 100%">
              <el-option label="USD" value="USD" />
              <el-option label="EUR" value="EUR" />
              <el-option label="KRW" value="KRW" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="금액" prop="amount">
            <MoneyInput v-model="form.amount" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="과부족(±%)">
            <el-input-number v-model="form.tolerance_plus" :min="0" :max="100" style="width: 45%" />
            /
            <el-input-number v-model="form.tolerance_minus" :min="0" :max="100" style="width: 45%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="개설은행" prop="issuing_bank">
            <el-input v-model="form.issuing_bank" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="통지은행">
            <el-input v-model="form.advising_bank" />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 공급사 (2026-07-14 요청 #6) — 개설 시점 확정(업무 확인). 선택 시 수익자 자동 채움,
           수입송장에서 이 LC 선택 시 공급사가 자동으로 잡힌다 -->
      <el-row :gutter="20">
        <el-col :span="24">
          <el-form-item label="공급사" prop="supplier">
            <el-select v-model="form.supplier" filterable clearable placeholder="공급사 선택 — 수익자 자동 채움"
              style="width: 100%" @change="onSupplierSelected">
              <el-option v-for="s in suppliers" :key="s.supplier_id" :label="s.name" :value="s.supplier_id" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="개설의뢰인" prop="applicant">
            <el-input v-model="form.applicant" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="수익자" prop="beneficiary">
            <el-input v-model="form.beneficiary" />
          </el-form-item>
        </el-col>
      </el-row>

      <!-- 사후 등록 (2026-07-14) — 실무는 은행이 이미 발행한 LC를 등록하므로
           초안→개설요청→개설완료 2클릭 없이 저장과 동시에 개설완료 처리 -->
      <el-form-item v-if="!props.editData" label="등록 방식">
        <el-checkbox v-model="registerAsIssued">
          저장 시 바로 '개설완료'로 등록 — 이미 은행에서 개설된 LC (개설일 필수)
        </el-checkbox>
        <div class="reg-hint">
          연결 지출결의서가 집행 전이면 초안으로 저장됩니다. 체크 해제 시 초안 → 개설요청 → 개설완료 단계를 사용합니다.
        </div>
      </el-form-item>

      <el-row :gutter="20">
        <!-- 개설일 (2026-07-14 요청 #11) — 과거 개설 건 이관 입력용. 비우면 '개설완료' 처리 시점에 자동 -->
        <el-col :span="8">
          <el-form-item label="개설일" prop="issue_date">
            <el-date-picker v-model="form.issue_date" type="date" value-format="YYYY-MM-DD"
              :placeholder="registerAsIssued && !props.editData ? '개설완료 등록에 필수' : '비우면 개설완료 시 자동'"
              style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="유효기일" prop="expiry_date">
            <el-date-picker v-model="form.expiry_date" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="최종선적일" prop="latest_shipment_date">
            <el-date-picker v-model="form.latest_shipment_date" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20" v-if="form.lc_type === 'USANCE'">
        <el-col :span="12">
          <el-form-item label="유산스 기간" prop="usance_days">
            <el-input-number v-model="form.usance_days" :min="0" style="width: 100%" />
            <span style="margin-left: 5px">일</span>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="선적항">
            <el-input v-model="form.port_of_loading" placeholder="예: VNHPH" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="양륙항">
            <el-input v-model="form.port_of_discharge" placeholder="예: KRINC" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="6">
          <el-form-item label="분할선적">
            <el-switch v-model="form.partial_shipment" />
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="환적">
            <el-switch v-model="form.transshipment" />
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="양도가능">
            <el-switch v-model="form.is_transferable" />
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="제3자서류">
            <el-switch v-model="form.third_party_docs" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-collapse style="margin: 15px 0">
        <el-collapse-item title="비용/환율" name="cost">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="개설환율">
                <el-input-number v-model="form.opening_exchange_rate" :min="0" :precision="4" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="상환금액">
                <MoneyInput v-model="form.repayment_amount" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="유산스이자">
                <MoneyInput v-model="form.usance_interest" style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="8">
              <el-form-item label="인수수수료">
                <MoneyInput v-model="form.acceptance_fee" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="개설수수료">
                <MoneyInput v-model="form.opening_fee" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="조건변경수수료">
                <MoneyInput v-model="form.amendment_fee" style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
        </el-collapse-item>

        <el-collapse-item title="보험" name="insurance">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="적하보험료">
                <MoneyInput v-model="form.cargo_insurance_premium" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="보험 증권번호">
                <el-input v-model="form.cargo_insurance_policy_number" />
              </el-form-item>
            </el-col>
          </el-row>
        </el-collapse-item>

        <el-collapse-item title="무역조건" name="trade">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="가격조건">
                <el-input v-model="form.price_terms" placeholder="FOB, CIF 등" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="매입은행">
                <el-input v-model="form.negotiating_bank" />
              </el-form-item>
            </el-col>
          </el-row>
          <!-- 지출결의서 번호 — 다건 선택 식별을 위해 전체 폭 + 태그 펼침 (2026-07-09) -->
          <el-row :gutter="20">
            <el-col :span="24">
              <el-form-item label="지출결의서 번호">
                <el-select v-model="form.voucher_numbers"
                  multiple filterable allow-create default-first-option clearable
                  placeholder="결제방법 LC인 지출결의서 선택(여러 건, 없어도 개설 가능 — 집행 전 건은 개설완료 시점에 집행돼 있어야 함)"
                  class="voucher-select"
                  style="width: 100%"
                  @change="onVouchersSelected">
                  <el-option
                    v-for="v in lcVouchers"
                    :key="v.id"
                    :label="`${v.document_number}${v.partner_name ? ' · ' + v.partner_name : ''} · ${v.currency} ${Number(v.display_amount ?? v.amount ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} (${v.status_display})`"
                    :value="v.document_number"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="선적시작일">
                <el-date-picker v-model="form.shipment_date_start" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="선적종료일">
                <el-date-picker v-model="form.shipment_date_end" type="date" value-format="YYYY-MM-DD" style="width: 100%" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="LG 원본회수">
                <el-switch v-model="form.lg_original_recovered" />
              </el-form-item>
            </el-col>
          </el-row>
        </el-collapse-item>
      </el-collapse>

      <el-form-item label="비고">
        <el-input v-model="form.remarks" type="textarea" :rows="3" />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="visible = false">취소</el-button>
      <el-button type="primary" @click="handleSubmit">저장</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import MoneyInput from '@/components/common/MoneyInput.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  defaultData: {
    type: Object,
    default: null
  },
  editData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const codeStore = useCodeStore()
const visible = ref(props.modelValue)
const formRef = ref(null)
// 사후 등록 (2026-07-14) — 신규 저장 시 바로 개설완료. 실무 기본 흐름이라 기본 ON
const registerAsIssued = ref(true)
const mainRequests = ref([])
const facilityAccounts = ref([])
const lcVouchers = ref([])
const suppliers = ref([])

const form = reactive({
  lc_number: '',
  lc_type: 'AT_SIGHT',
  company: null,
  main_request: null,
  facility_account: null,
  supplier: null,
  currency: 'USD',
  amount: 0,
  tolerance_plus: 10,
  tolerance_minus: 10,
  issuing_bank: '',
  advising_bank: '',
  applicant: '',
  beneficiary: '',
  issue_date: '',
  expiry_date: '',
  latest_shipment_date: '',
  usance_days: null,
  port_of_loading: '',
  port_of_discharge: '',
  partial_shipment: true,
  transshipment: true,
  is_transferable: false,
  third_party_docs: false,
  // 비용/환율
  opening_exchange_rate: null,
  repayment_amount: null,
  usance_interest: null,
  acceptance_fee: null,
  opening_fee: null,
  amendment_fee: null,
  // 보험
  cargo_insurance_premium: null,
  cargo_insurance_policy_number: '',
  // 무역조건
  price_terms: '',
  negotiating_bank: '',
  expense_voucher_number: '',
  voucher_numbers: [],
  shipment_date_start: '',
  shipment_date_end: '',
  lg_original_recovered: false,
  remarks: ''
})

// 생성 폼 기본값 스냅샷 (수정→생성 재사용 시 초기화용)
const DEFAULTS = { ...form }
const NUM_FIELDS = ['amount', 'tolerance_plus', 'tolerance_minus', 'usance_days',
  'opening_exchange_rate', 'repayment_amount', 'usance_interest', 'acceptance_fee',
  'opening_fee', 'amendment_fee', 'cargo_insurance_premium']
const fillForm = (data) => {
  Object.keys(form).forEach(k => {
    if (k === 'voucher_numbers') return  // 아래에서 별도 처리
    if (data[k] !== undefined && data[k] !== null) {
      form[k] = NUM_FIELDS.includes(k) ? Number(data[k]) : data[k]
    }
  })
  // 연결 지출결의서(1:N): 상세의 vouchers 배열 → 번호 목록. 없으면 레거시 단일필드 fallback
  let nums = (data.vouchers || []).map(v => v.document_number).filter(Boolean)
  if (!nums.length && data.expense_voucher_number) nums = [data.expense_voucher_number]
  form.voucher_numbers = nums
}

const rules = {
  lc_number: [{ required: true, message: 'LC번호를 입력하세요', trigger: 'blur' }],
  // 메인오더(메인요청) 연결은 선택값 — LC는 메인오더 없이도 개설 가능
  amount: [{ required: true, message: '금액을 입력하세요', trigger: 'blur' }],
  issuing_bank: [{ required: true, message: '개설은행을 입력하세요', trigger: 'blur' }],
  applicant: [{ required: true, message: '개설의뢰인을 입력하세요', trigger: 'blur' }],
  beneficiary: [{ required: true, message: '수익자를 입력하세요', trigger: 'blur' }],
  expiry_date: [{ required: true, message: '유효기일을 선택하세요', trigger: 'change' }]
}

const fetchMainRequests = async () => {
  try {
    const response = await axiosInstance.get('production/main-requests/')
    mainRequests.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch main requests:', error)
  }
}

const fetchFacilityAccounts = async () => {
  try {
    const response = await axiosInstance.get('finance/bank-accounts/', { params: { active: 1 } })
    const list = response.data.results || response.data
    // 수입신용장 여신한도 = 차입금(LOAN) 계좌
    facilityAccounts.value = (list || []).filter(a => a.account_type === 'LOAN')
  } catch (error) {
    console.error('Failed to fetch facility accounts:', error)
  }
}

// 차입금 계좌 선택 시 개설은행/통화 자동 채움 (한도 통화 일치)
const fetchLcVouchers = async () => {
  try {
    const response = await axiosInstance.get('expenses/lc-vouchers/')
    lcVouchers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch LC vouchers:', error)
  }
}

const fetchSuppliers = async () => {
  try {
    const response = await axiosInstance.get('suppliers/')
    suppliers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch suppliers:', error)
  }
}

// 공급사 선택 → 수익자 자동 채움 (2026-07-14 #6) — LC 서류 특성상 영문명 우선.
// 공급사를 고르는 행위 = 수익자 확정 의도로 보고 덮어씀 (이후 직접 수정 가능).
const onSupplierSelected = (supplierId) => {
  const s = suppliers.value.find(x => x.supplier_id === supplierId)
  if (!s) return
  form.beneficiary = s.english_name || s.name || form.beneficiary
}

const onFacilitySelected = (accountId) => {
  const acc = facilityAccounts.value.find(a => a.id === accountId)
  if (!acc) return
  if (acc.bank) form.issuing_bank = acc.bank
  if (acc.currency) form.currency = acc.currency
}

// 한도계좌 법인 필터 (2026-07-14 요청 #12) — 법인 선택 시 그 법인의 차입금 계좌만 노출.
// 법인 미선택이면 전체 노출(기존 동작 유지). 편집 중 기존 선택 계좌는 법인이 달라도 목록에 유지(숨기면 값이 유실돼 보임).
const filteredFacilityAccounts = computed(() => {
  if (!form.company) return facilityAccounts.value
  return facilityAccounts.value.filter(
    a => a.company === form.company || a.id === form.facility_account
  )
})

// 법인 선택 → 개설의뢰인 자동 채움 (2026-07-09) — LC 서류 특성상 영문명 우선, 없으면 한글명.
// 사용자가 법인을 바꾸는 행위 = 개설의뢰인 변경 의도로 보고 덮어씀 (이후 직접 수정 가능).
const onCompanySelected = (companyId) => {
  const c = codeStore.ourCompanys.find(x => x.id === companyId)
  // 타법인 한도계좌가 선택돼 있으면 해제 (2026-07-14 #12)
  if (form.facility_account) {
    const acc = facilityAccounts.value.find(a => a.id === form.facility_account)
    if (acc && companyId && acc.company !== companyId) form.facility_account = null
  }
  if (!c) return
  form.applicant = c.name_en || c.name || form.applicant
}

// 지출결의서 선택 → 수익자 자동 채움 (2026-07-09) — 첫 선택 건의 거래처(영문명 우선).
// 수익자가 비어 있을 때만 채움 — 여러 지결의 거래처가 다르거나 직접 입력한 값을 덮지 않기 위함.
const onVouchersSelected = (numbers) => {
  if (form.beneficiary || !numbers?.length) return
  for (const num of numbers) {
    const v = lcVouchers.value.find(x => x.document_number === num)
    if (v && (v.partner_english_name || v.partner_name)) {
      form.beneficiary = v.partner_english_name || v.partner_name
      return
    }
  }
}

// ── 잔여한도 미리보기 (2026-07-09) ──
const fmtLimit = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })
const facilityLabel = (acc) => {
  let s = acc.name
  if (acc.credit_limit) s += ` · 설정한도 ${fmtLimit(acc.credit_limit)}`
  if (acc.lc_available !== null && acc.lc_available !== undefined) s += ` · 잔여 ${fmtLimit(acc.lc_available)}`
  if (acc.currency) s += ` ${acc.currency}`
  return s
}
// 개설~상환 전 상태의 LC는 이미 사용한도(lc_outstanding)에 포함되어 있음
const OUTSTANDING_STATUSES = ['ISSUED', 'AMENDED', 'SHIPPED', 'NEGOTIATED']
const facilityPreview = computed(() => {
  const acc = facilityAccounts.value.find(a => a.id === form.facility_account)
  if (!acc || acc.credit_limit === null || acc.credit_limit === undefined) return null
  const limit = Number(acc.credit_limit) || 0
  const used = Number(acc.lc_outstanding) || 0
  const available = acc.lc_available !== null && acc.lc_available !== undefined
    ? Number(acc.lc_available) : limit - used
  const amount = Number(form.amount) || 0
  const alreadyCounted = OUTSTANDING_STATUSES.includes(props.editData?.status)
  const after = available - amount
  return {
    currency: acc.currency,
    limit,
    used,
    available,
    after,
    alreadyCounted,
    over: !alreadyCounted && after < 0,
    currencyMismatch: !!(acc.currency && form.currency && acc.currency !== form.currency),
  }
})

const handleSubmit = async () => {
  await formRef.value.validate()

  // 사후 등록(개설완료로 등록)은 개설일 필수 (2026-07-14)
  if (!props.editData && registerAsIssued.value && !form.issue_date) {
    ElMessage.warning("'개설완료로 등록'하려면 개설일을 입력하세요. (초안으로만 저장하려면 체크를 해제)")
    return
  }

  // 빈 날짜 칸('')은 DRF가 거부하므로 null 로 변환
  const payload = { ...form }
  const dateFields = ['issue_date', 'expiry_date', 'latest_shipment_date', 'shipment_date_start', 'shipment_date_end']
  dateFields.forEach(f => { if (!payload[f]) payload[f] = null })

  try {
    if (props.editData) {
      await axiosInstance.patch(`lcs/${props.editData.id}/`, payload)
      ElMessage.success('LC가 수정되었습니다.')
    } else {
      payload.register_as_issued = registerAsIssued.value
      const res = await axiosInstance.post('lcs/', payload)
      ElMessage.success(res.data?.message || 'LC가 생성되었습니다.')
      // 사후 등록 결과: 게이트 미통과 등으로 초안 유지 시 사유 안내
      if (res.data?.issue_hold) ElMessage.warning(res.data.issue_hold)
      if (res.data?.warning) ElMessage.warning(res.data.warning)
    }
    emit('saved')
    visible.value = false
  } catch (error) {
    const data = error?.response?.data
    const msg = data ? Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : v}`).join('\n') : '저장에 실패했습니다.'
    ElMessage.error(msg)
    console.error(error)
  }
}

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val) {
    // 다이얼로그가 열릴 때마다 옵션을 새로 로드 — 마운트 이후 추가된 계좌/메인오더 반영
    fetchFacilityAccounts()
    fetchMainRequests()
    fetchLcVouchers()
    fetchSuppliers()
    if (props.editData) {
      fillForm(props.editData)
    } else {
      Object.assign(form, DEFAULTS)
      form.voucher_numbers = []  // 배열은 참조 공유 방지 위해 새로 초기화
      registerAsIssued.value = true  // 사후 등록이 실무 기본 (2026-07-14)
      if (props.defaultData && props.defaultData.main_request) {
        form.main_request = props.defaultData.main_request
      }
    }
  }
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

onMounted(() => {
  fetchMainRequests()
  fetchFacilityAccounts()
  fetchLcVouchers()
  fetchSuppliers()
  codeStore.fetchOurCompanys()
})
</script>

<style scoped>
/* 지출결의서 다건 태그 (2026-07-09) — 태그 안 번호·금액이 말줄임으로 잘리지 않게 */
.voucher-select :deep(.el-select__tags-text) {
  max-width: none !important;
}

/* 잔여한도 미리보기 (2026-07-09) */
.facility-preview {
  font-size: 12px; color: #606266; line-height: 1.6; margin-top: 2px;
}
.facility-preview b { color: #303133; }
.facility-preview .fp-over { color: #f56c6c; font-weight: 600; }
.facility-preview.fp-hint { color: #909399; }

/* 사후 등록 안내 (2026-07-14) */
.reg-hint { font-size: 12px; color: #909399; line-height: 1.5; }
</style>
